import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Query, QueryClient } from '@tanstack/react-query';
import { realtimeEventTypes } from '@qronstat/shared';
import type { RealtimeEventType } from '@qronstat/shared';
import { useOnline } from './useOnline';
import { checkForUpdate, isNewerBuild } from './pwa';

export type RealtimeStatus = 'connecting' | 'open' | 'closed';

const RealtimeContext = createContext<RealtimeStatus>('connecting');

export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeContext);
}

const dataRoots = new Set([
  'q',
  'objects',
  'freshness',
  'records',
  'drill',
  'object-history',
  'object-groups',
]);

function keyText(query: Query): string {
  return JSON.stringify(query.queryKey);
}

export const realtimeTargets: Record<RealtimeEventType, (query: Query) => boolean> = {
  data: (query) => dataRoots.has(String(query.queryKey[0])),
  live: (query) =>
    query.meta?.live === true ||
    (dataRoots.has(String(query.queryKey[0])) && keyText(query).includes('station_state')),
  alerts: (query) => ['alerts-feed', 'alerts-unread'].includes(String(query.queryKey[0])),
  exports: (query) => query.queryKey[0] === 'exports',
  layouts: (query) => query.queryKey[0] === 'layouts',
  telegram: (query) => ['telegram-status', 'telegram-deliveries'].includes(String(query.queryKey[0])),
};

function refresh(client: QueryClient, type: RealtimeEventType): void {
  void client.invalidateQueries({ predicate: realtimeTargets[type] });
}

const firstRetryMs = 2_000;
const lastRetryMs = 60_000;

export function parseBuild(data: string): string | undefined {
  try {
    const parsed = JSON.parse(data) as { build?: unknown };
    return typeof parsed.build === 'string' ? parsed.build : undefined;
  } catch {
    return undefined;
  }
}

export function reconnectDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(lastRetryMs, firstRetryMs * 2 ** Math.max(0, attempt - 1));
  return Math.round(base * (0.8 + 0.4 * random()));
}

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const online = useOnline();
  const [status, setStatus] = useState<RealtimeStatus>('connecting');
  useEffect(() => {
    if (!online || typeof EventSource === 'undefined') {
      setStatus('closed');
      return;
    }
    let source: EventSource | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;
    const connect = () => {
      setStatus('connecting');
      const current = new EventSource('/api/events');
      source = current;
      current.onopen = () => {
        if (attempt > 0) for (const type of realtimeEventTypes) refresh(client, type);
        attempt = 0;
        setStatus('open');
      };
      current.onerror = () => {
        if (current.readyState !== EventSource.CLOSED) {
          setStatus('connecting');
          return;
        }
        current.close();
        setStatus('closed');
        if (stopped) return;
        attempt += 1;
        timer = setTimeout(connect, reconnectDelay(attempt));
      };
      for (const type of realtimeEventTypes)
        current.addEventListener(type, (event: MessageEvent<string>) => {
          setStatus('open');
          refresh(client, type);
          if (type === 'live' && isNewerBuild(parseBuild(event.data))) checkForUpdate();
        });
    };
    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      source?.close();
    };
  }, [client, online]);
  return <RealtimeContext value={status}>{children}</RealtimeContext>;
}
